import React from 'react';
import { Supplier } from '@/features/partner/supplierStatus/types/supplier.types';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import WeekSelector from '@/shared/components/forms/WeekSelector';

interface DashboardFilterSectionProps {
    isOpen: boolean;
    onToggle: () => void;
    userType: string;
    suppliers: Supplier[];
    selectedSupplierId: string;
    onSupplierChange: (id: string) => void;
    viewMode: 'weekly' | 'realtime';
    fromDate: string;
    toDate: string;
    onDateChange: (start: string, end: string) => void;
    slotCountFilter: string;
    onSlotCountChange: (v: string) => void;
    acceptRateFilter: string;
    onAcceptRateChange: (v: string) => void;
    onSearch: () => void;
    onReset: () => void;
}

const DashboardFilterSection: React.FC<DashboardFilterSectionProps> = ({
                                                                           isOpen,
                                                                           onToggle,
                                                                           userType,
                                                                           suppliers,
                                                                           selectedSupplierId,
                                                                           onSupplierChange,
                                                                           viewMode,
                                                                           fromDate,
                                                                           toDate,
                                                                           onDateChange,
                                                                           slotCountFilter,
                                                                           onSlotCountChange,
                                                                           acceptRateFilter,
                                                                           onAcceptRateChange,
                                                                           onSearch,
                                                                           onReset,
                                                                       }) => {
    const activeCount = [
        userType === 'HEADQUARTERS' && selectedSupplierId,
        slotCountFilter,
        acceptRateFilter,
    ].filter(Boolean).length;

    return (
        <div className="card search-card">
            <div className="card-header filter-header" onClick={onToggle}>
                <span className="filter-title">
                    <i className="fas fa-filter" />
                    협력사 필터 조건
                    {activeCount > 0 && (
                        <span className="dashboard-filter-badge">{activeCount}</span>
                    )}
                </span>
                <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} filter-toggle-icon`} />
            </div>

            {isOpen && (
                <div className="card-body">
                    <div className={`search-form dashboard-search-form ${userType === 'HEADQUARTERS' ? '' : 'supplier-view'} ${viewMode === 'weekly' ? 'weekly-mode' : ''}`}>

                        {/* 협력사 선택 - 본사만 */}
                        {userType === 'HEADQUARTERS' && (
                            <div className="form-group">
                                <div className={`form-field ${selectedSupplierId ? 'has-value' : ''}`}>
                                    <label className="form-label">
                                        <i className="fas fa-building" />
                                        협력사
                                    </label>
                                    <SearchableSelect
                                        options={suppliers.map((s: Supplier): SelectOption => ({
                                            value: s.id,
                                            label: s.name,
                                        }))}
                                        value={selectedSupplierId}
                                        onChange={onSupplierChange}
                                        placeholder="전체 협력사"
                                        showAllOption={true}
                                        allOptionLabel="전체"
                                    />
                                </div>
                            </div>
                        )}

                        {/* 주차 선택 - 주별 모드에서만 */}
                        {viewMode === 'weekly' && (
                            <div className="form-group">
                                <div className="form-field">
                                    <label className="form-label">
                                        <i className="fas fa-calendar-week" />
                                        조회 기간
                                    </label>
                                    <WeekSelector
                                        value={{ startDate: fromDate, endDate: toDate }}
                                        onChange={async (startDate, endDate) => onDateChange(startDate, endDate)}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 완료 건수 필터 */}
                        <div className="form-group">
                            <div className={`form-field ${slotCountFilter ? 'has-value' : ''}`}>
                                <label className="form-label">
                                    <i className="fas fa-check-circle" />
                                    완료 건수 이상 (목표 슬롯 기준)
                                </label>
                                <div className="dashboard-input-row">
                                    <div className="dashboard-number-input-wrap">
                                        <input
                                            type="number"
                                            className="text-input"
                                            placeholder="건수 입력"
                                            value={slotCountFilter}
                                            onChange={(e) => onSlotCountChange(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                                            min="0"
                                        />
                                        {slotCountFilter && (
                                            <span className="dashboard-input-suffix">건</span>
                                        )}
                                    </div>
                                    {slotCountFilter && (
                                        <button
                                            type="button"
                                            className="dashboard-filter-clear-btn"
                                            onClick={() => onSlotCountChange('')}
                                            title="초기화"
                                        >
                                            <i className="fas fa-times" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 수락률 필터 */}
                        <div className="form-group">
                            <div className={`form-field ${acceptRateFilter ? 'has-value' : ''}`}>
                                <label className="form-label">
                                    <i className="fas fa-percentage" />
                                    수락률 이상
                                </label>
                                <div className="dashboard-input-row">
                                    <div className="dashboard-number-input-wrap">
                                        <input
                                            type="number"
                                            className="text-input"
                                            placeholder="% 입력"
                                            value={acceptRateFilter}
                                            onChange={(e) => onAcceptRateChange(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                                            min="0"
                                            max="100"
                                        />
                                        {acceptRateFilter && (
                                            <span className="dashboard-input-suffix">%</span>
                                        )}
                                    </div>
                                    {acceptRateFilter && (
                                        <button
                                            type="button"
                                            className="dashboard-filter-clear-btn"
                                            onClick={() => onAcceptRateChange('')}
                                            title="초기화"
                                        >
                                            <i className="fas fa-times" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 버튼 그룹 */}
                        <div className="form-group button-group">
                            <button type="button" className="btn btn-secondary" onClick={onReset}>
                                <i className="fas fa-redo" />
                                초기화
                            </button>
                            <button type="button" className="btn btn-primary" onClick={onSearch}>
                                <i className="fas fa-search" />
                                검색
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardFilterSection;
